import React from 'react';
import { Box, Container, Grid, Typography, Button, Card, Stack } from '@mui/material';
import {
    TrackChanges, Visibility, Favorite, Flag, Translate,
    CurrencyRupee, Smartphone, Groups, Code, SupportAgent,
    RocketLaunch, Email, ArrowForward
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

export default function AboutUs() {
    const primaryGradient = 'linear-gradient(135deg, #FF6A00 0%, #EE0979 100%)';
    const darkBg = '#070B14';
    const cardBg = 'rgba(255, 255, 255, 0.9)';

    return (
        <Box sx={{ bgcolor: '#f8fafc', overflow: 'hidden' }}>

            {/* ==========================================
          1. HERO SECTION (FIXED IMAGE & SPACING)
          ========================================== */}
            <Box sx={{ position: 'relative', pt: { xs: 10, md: 15 }, pb: { xs: 8, md: 12 }, overflow: 'hidden' }}>
                {/* Decorative Backgrounds */}
                <Box sx={{ position: 'absolute', top: -100, right: -100, width: 400, height: 400, background: 'radial-gradient(circle, rgba(255,106,0,0.1) 0%, rgba(255,255,255,0) 70%)', borderRadius: '50%' }} />
                <Box sx={{ position: 'absolute', bottom: -50, left: -50, width: 300, height: 300, background: 'radial-gradient(circle, rgba(238,9,121,0.1) 0%, rgba(255,255,255,0) 70%)', borderRadius: '50%' }} />

                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                    <Grid container spacing={6} alignItems="center">

                        {/* Left Content Area */}
                        <Grid item xs={12} lg={6}>
                            <Typography variant="subtitle1" fontWeight="800" sx={{ color: '#FF6A00', mb: 2, letterSpacing: '2px', textTransform: 'uppercase' }}>
                                Our Story & Mission
                            </Typography>

                            <Typography variant="h2" fontWeight="900" sx={{ mb: 3, color: '#1E293B', letterSpacing: '-1px', lineHeight: 1.2, fontSize: { xs: '2.8rem', md: '3.8rem' } }}>
                                About <span style={{ background: primaryGradient, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>PracharApp</span>
                            </Typography>

                            <Typography variant="h6" sx={{ color: '#475569', mb: 3, lineHeight: 1.6, fontWeight: 600 }}>
                                Empowering political leaders and party members across India with professional-quality poster and video creation tools.
                            </Typography>

                            <Typography variant="body1" sx={{ color: '#64748B', mb: 5, fontSize: '1.1rem', lineHeight: 1.8 }}>
                                We believe that every political voice deserves to be heard with clarity, impact, and absolute professionalism.
                            </Typography>

                            <Button
                                variant="contained"
                                size="large"
                                endIcon={<ArrowForward />}
                                sx={{
                                    background: primaryGradient, color: 'white', px: 4, py: 1.5,
                                    borderRadius: '50px', fontWeight: '800', fontSize: '1.1rem',
                                    boxShadow: '0 10px 25px rgba(238, 9, 121, 0.4)',
                                    transition: 'all 0.3s', '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 15px 35px rgba(238, 9, 121, 0.5)' }
                                }}
                            >
                                Join Our Mission
                            </Button>
                        </Grid>

                        {/* Right Image Area (Fixed Broken Image Issue) */}
                        <Grid item xs={12} lg={6}>
                            <Box sx={{ position: 'relative', width: '100%', mt: { xs: 4, lg: 0 } }}>
                                {/* Background Shadow Box */}
                                <Box sx={{ position: 'absolute', inset: 0, background: primaryGradient, borderRadius: '24px', transform: 'rotate(4deg)', opacity: 0.2 }} />

                                {/* Proper Image Container */}
                                <Box
                                    sx={{
                                        position: 'relative', zIndex: 1, width: '100%', height: { xs: '300px', md: '450px' },
                                        bgcolor: '#ffffff', borderRadius: '24px', overflow: 'hidden',
                                        boxShadow: '0 20px 40px rgba(0,0,0,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        border: '4px solid white'
                                    }}
                                >
                                    {/* Using a solid placeholder image from Unsplash so it never looks broken, you can change the src back to /public/images/app.png when ready */}
                                    <img
                                        src="https://images.unsplash.com/photo-1555436169-20e93ea9a7ff?q=80&w=800&auto=format&fit=crop"
                                        alt="PracharApp Platform"
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        onError={(e) => {
                                            // If image fails, it shows a nice gradient instead of broken alt text
                                            e.target.style.display = 'none';
                                            e.target.parentElement.style.background = 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)';
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Grid>

                    </Grid>
                </Container>
            </Box>

            {/* ==========================================
          2. MISSION / VISION / VALUES 
          ========================================== */}
            <Container maxWidth="lg" sx={{ mt: { xs: 2, md: -6 }, position: 'relative', zIndex: 2, pb: { xs: 4, md: 6 } }}>
                <Grid container spacing={2}>
                    {/* Mission */}
                    <Grid item xs={12} md={4}>
                        <Card sx={{ p: 2.5, textAlign: 'center', borderRadius: 4, bgcolor: cardBg, backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.8)', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 15px 30px rgba(238, 9, 121, 0.15)' } }}>
                            <Box sx={{ width: 50, height: 50, mx: 'auto', mb: 1.5, borderRadius: '50%', background: 'linear-gradient(135deg, rgba(255,106,0,0.1) 0%, rgba(238,9,121,0.1) 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#EE0979' }}>
                                <TrackChanges sx={{ fontSize: 28 }} />
                            </Box>
                            <Typography variant="h6" fontWeight="800" mb={0.5} color="#1E293B">Our Mission</Typography>
                            <Typography variant="body2" color="#64748B" lineHeight={1.4}>To democratize political communication by providing accessible, professional-grade design tools for every leader.</Typography>
                        </Card>
                    </Grid>

                    {/* Vision */}
                    <Grid item xs={12} md={4}>
                        <Card sx={{ p: 2.5, textAlign: 'center', borderRadius: 4, bgcolor: cardBg, backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.8)', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 15px 30px rgba(238, 9, 121, 0.15)' } }}>
                            <Box sx={{ width: 50, height: 50, mx: 'auto', mb: 1.5, borderRadius: '50%', background: 'linear-gradient(135deg, rgba(255,106,0,0.1) 0%, rgba(238,9,121,0.1) 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#EE0979' }}>
                                <Visibility sx={{ fontSize: 28 }} />
                            </Box>
                            <Typography variant="h6" fontWeight="800" mb={0.5} color="#1E293B">Our Vision</Typography>
                            <Typography variant="body2" color="#64748B" lineHeight={1.4}>To become India's leading platform for political communication, fostering transparent and engaging democratic discourse.</Typography>
                        </Card>
                    </Grid>

                    {/* Values */}
                    <Grid item xs={12} md={4}>
                        <Card sx={{ p: 2.5, textAlign: 'center', borderRadius: 4, bgcolor: cardBg, backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.8)', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 15px 30px rgba(238, 9, 121, 0.15)' } }}>
                            <Box sx={{ width: 50, height: 50, mx: 'auto', mb: 1.5, borderRadius: '50%', background: 'linear-gradient(135deg, rgba(255,106,0,0.1) 0%, rgba(238,9,121,0.1) 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#EE0979' }}>
                                <Favorite sx={{ fontSize: 28 }} />
                            </Box>
                            <Typography variant="h6" fontWeight="800" mb={0.5} color="#1E293B">Our Values</Typography>
                            <Typography variant="body2" color="#64748B" lineHeight={1.4}>Integrity, accessibility, innovation, and empowerment. Supporting democratic processes through technology.</Typography>
                        </Card>
                    </Grid>
                </Grid>
            </Container>


            {/* ==========================================
          3. IMPACT / STATS SECTION (OVERLAP & SPACE FIXED)
          ========================================== */}
            <Box sx={{ bgcolor: darkBg, py: { xs: 5, md: 8 } }}>
                <Container maxWidth="lg">

                    {/* हेडिंग के नीचे का गैप mb={4} से घटाकर mb={2} कर दिया है */}
                    <Box textAlign="center" mb={2}>
                        <Typography variant="h4" fontWeight="900" color="white" mb={0.5}>
                            Our Impact
                        </Typography>
                        <Typography variant="body2" color="#94A3B8">
                            Numbers that tell our story
                        </Typography>
                    </Box>

                    {/* Grid spacing={2} का यूज़ किया है, यह 100% सेफ है और कभी ओवरलैप नहीं करेगा */}
                    <Grid container spacing={2} justifyContent="center">
                        {[
                            { num: "10K+", label: "Active Users" },
                            { num: "50K+", label: "Posters Created" },
                            { num: "500+", label: "Templates" },
                            { num: "28", label: "States Covered" }
                        ].map((stat, i) => (
                            <Grid item xs={6} md={3} key={i}>
                                {/* Overlap रोकने के लिए height: '100%' हटा दिया गया है */}
                                <Box
                                    sx={{
                                        p: 2,
                                        textAlign: 'center',
                                        bgcolor: 'rgba(255,255,255,0.03)',
                                        borderRadius: '16px',
                                        border: '1px solid rgba(255,255,255,0.05)',
                                        transition: 'transform 0.3s',
                                        '&:hover': { bgcolor: 'rgba(255,255,255,0.08)', transform: 'translateY(-3px)' }
                                    }}
                                >
                                    <Typography
                                        variant="h4"
                                        fontWeight="900"
                                        sx={{
                                            background: primaryGradient,
                                            WebkitBackgroundClip: 'text',
                                            WebkitTextFillColor: 'transparent',
                                            mb: 0.5
                                        }}
                                    >
                                        {stat.num}
                                    </Typography>
                                    <Typography variant="body2" color="white" fontWeight="600">
                                        {stat.label}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>

                </Container>
            </Box>

            {/* ==========================================
          4. CTA SECTION
          ========================================== */}
            <Box sx={{ py: 10, bgcolor: '#f8fafc' }}>
                <Container maxWidth="md">
                    <Box
                        sx={{
                            background: primaryGradient, borderRadius: 6, p: { xs: 5, md: 8 },
                            textAlign: 'center', color: 'white', boxShadow: '0 20px 40px rgba(238, 9, 121, 0.3)',
                            position: 'relative', overflow: 'hidden'
                        }}
                    >
                        <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: '50%' }} />
                        <Box sx={{ position: 'relative', zIndex: 1 }}>
                            <Typography variant="h3" fontWeight="900" mb={2}>Join Our Mission</Typography>
                            <Typography variant="h6" mb={5} fontWeight="500" sx={{ opacity: 0.9 }}>
                                Be part of the movement to democratize political communication in India.
                            </Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                                <Button
                                    component={Link} to="/pricing"
                                    variant="contained" size="large" startIcon={<RocketLaunch />}
                                    sx={{ bgcolor: 'white', color: '#EE0979', px: 5, py: 1.5, borderRadius: '50px', fontWeight: '900', fontSize: '1.1rem', '&:hover': { bgcolor: '#f8fafc', transform: 'translateY(-2px)' } }}
                                >
                                    Get Started
                                </Button>
                                <Button
                                    component="a" href="mailto:hello@pracharapp.com"
                                    variant="outlined" size="large" startIcon={<Email />}
                                    sx={{ borderColor: 'rgba(255,255,255,0.6)', color: 'white', px: 5, py: 1.5, borderRadius: '50px', fontWeight: '800', fontSize: '1.1rem', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)', transform: 'translateY(-2px)' } }}
                                >
                                    Contact Us
                                </Button>
                            </Stack>
                        </Box>
                    </Box>
                </Container>
            </Box>

        </Box>
    );
}